import React, { useState, useEffect, useCallback } from 'react'
import {
  getPartnerMappingRules,
  PartnerMappingRules,
  Partner
} from '../../../../../mogul/core/_requests'
import { APIError } from '../../../../../app/modules/auth'
import usePartners from '../../../../../mogul/hooks/usePartners'

interface PartnerMappingRulesAdminProps {}

export function PartnerMappingRulesAdmin({}: PartnerMappingRulesAdminProps) {
  const { partners, loading: partnersLoading } = usePartners(true)
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | undefined>()
  const [mappingRules, setMappingRules] = useState<PartnerMappingRules | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadMappingRules = useCallback(async (partnerId: number) => {
    setLoading(true)
    setError(null)
    try {
      const rules = await getPartnerMappingRules(partnerId)
      setMappingRules(rules)
    } catch (err) {
      console.error('Failed to load mapping rules:', err)
      const errorMessage = err instanceof APIError ? err.message : 'Failed to load mapping rules'
      setError(errorMessage)
      setMappingRules(null)
    } finally {
      setLoading(false)
    }
  }, [])

  const handlePartnerChange = (partnerId: number | undefined) => {
    setSelectedPartnerId(partnerId)
    setMappingRules(null)
    setError(null)

    if (partnerId) {
      loadMappingRules(partnerId)
    }
  }

  const getDiscoveryTypeDisplay = (type: string) => {
    switch (type) {
      case 'funeral_home_based':
        return {
          label: 'Funeral Home Based',
          description: 'Discovers entities based on funeral home data from webhook account information',
          color: 'primary'
        }
      case 'agent_based':
        return {
          label: 'Agent Based',
          description: 'Discovers entities based on agent data from webhook account information',
          color: 'success'
        }
      case 'custom':
        return {
          label: 'Custom',
          description: 'Uses custom discovery logic specific to this partner',
          color: 'warning'
        }
      default:
        return {
          label: type,
          description: 'Unknown discovery type',
          color: 'secondary'
        }
    }
  }

  const selectedPartner = partners.find(p => p.id === selectedPartnerId)

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Partner Mapping Rules Administration</h3>
        <div className="card-toolbar">
          <span className="badge badge-light-info">
            <i className="fas fa-info-circle me-2"></i>
            View & Manage Partner Discovery Rules
          </span>
        </div>
      </div>

      <div className="card-body">
        {/* Partner Selection */}
        <div className="row mb-8">
          <div className="col-md-6">
            <label className="form-label fw-bold">Select Partner</label>
            <select
              className="form-select form-select-lg"
              value={selectedPartnerId || ''}
              onChange={(e) => handlePartnerChange(e.target.value ? parseInt(e.target.value) : undefined)}
              disabled={partnersLoading}
            >
              <option value="">{partnersLoading ? 'Loading partners...' : 'Choose a partner to view mapping rules'}</option>
              {!partnersLoading && partners.filter(partner => partner.id && !isNaN(partner.id)).map(partner => (
                <option key={partner.unique_identifier} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
            {selectedPartner && (
              <div className="text-muted mt-2">
                <small>
                  <strong>ID:</strong> {selectedPartner.id} |
                  <strong> Unique ID:</strong> <code>{selectedPartner.unique_identifier}</code>
                </small>
              </div>
            )}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="text-center py-10">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading mapping rules...</span>
            </div>
            <div className="mt-3 text-muted">Loading mapping rules...</div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* No Partner Selected */}
        {!selectedPartnerId && !loading && (
          <div className="alert alert-info">
            <i className="fas fa-arrow-up me-2"></i>
            Select a partner above to view and manage their mapping rules.
          </div>
        )}

        {/* Mapping Rules Display */}
        {mappingRules && selectedPartner && (
          <>
            {/* Overview Cards */}
            <div className="row mb-8">
              <div className="col-md-4">
                <div className={`card border border-${getDiscoveryTypeDisplay(mappingRules.discovery_type).color}`}>
                  <div className="card-body text-center">
                    <i className="fas fa-search fs-2x text-primary mb-3"></i>
                    <h5 className="card-title">{getDiscoveryTypeDisplay(mappingRules.discovery_type).label}</h5>
                    <p className="text-muted small">{getDiscoveryTypeDisplay(mappingRules.discovery_type).description}</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border border-info">
                  <div className="card-body text-center">
                    <i className="fas fa-users fs-2x text-info mb-3"></i>
                    <h5 className="card-title">{mappingRules.entity_display_name_plural}</h5>
                    <p className="text-muted small">This partner discovers <strong>{mappingRules.entity_display_name_plural.toLowerCase()}</strong> from webhook data</p>
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="card border border-success">
                  <div className="card-body text-center">
                    <i className="fas fa-cogs fs-2x text-success mb-3"></i>
                    <h5 className="card-title">{Object.keys(mappingRules.supported_fields).length} Fields</h5>
                    <p className="text-muted small">Supported data fields for mapping</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Configuration Details */}
            <div className="row">
              {/* Entity Configuration */}
              <div className="col-md-6">
                <div className="card border border-light">
                  <div className="card-header">
                    <h4 className="card-title">
                      <i className="fas fa-id-card me-2"></i>
                      Entity Discovery Configuration
                    </h4>
                  </div>
                  <div className="card-body">
                    <table className="table table-row-bordered">
                      <tbody>
                        <tr>
                          <td className="fw-bold text-muted">Entity Type:</td>
                          <td>
                            <span className="badge badge-light-primary">
                              {mappingRules.entity_display_name}
                            </span>
                          </td>
                        </tr>
                        <tr>
                          <td className="fw-bold text-muted">Plural Form:</td>
                          <td>{mappingRules.entity_display_name_plural}</td>
                        </tr>
                        <tr>
                          <td className="fw-bold text-muted">Identifier Field:</td>
                          <td><code>{mappingRules.entity_identifier_field}</code></td>
                        </tr>
                        <tr>
                          <td className="fw-bold text-muted">Name Field:</td>
                          <td><code>{mappingRules.entity_name_field}</code></td>
                        </tr>
                        <tr>
                          <td className="fw-bold text-muted">Slice Group Template:</td>
                          <td>
                            <code>{mappingRules.slice_group_name_template}</code>
                            <div className="text-muted small mt-1">
                              Example: {mappingRules.slice_group_name_template.replace('{{entity_name}}', 'Sample ' + mappingRules.entity_display_name)}
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Supported Fields */}
              <div className="col-md-6">
                <div className="card border border-light">
                  <div className="card-header">
                    <h4 className="card-title">
                      <i className="fas fa-list me-2"></i>
                      Supported Data Fields
                    </h4>
                  </div>
                  <div className="card-body">
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table table-row-bordered table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Field Path</th>
                            <th>Type</th>
                            <th>Label</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(mappingRules.supported_fields).map(([fieldPath, config]) => (
                            <tr key={fieldPath}>
                              <td>
                                <code className="text-primary">{fieldPath}</code>
                              </td>
                              <td>
                                <span className={`badge badge-light-${
                                  config.type === 'email' ? 'info' :
                                  config.type === 'phone' ? 'warning' :
                                  config.type === 'date' ? 'success' :
                                  'secondary'
                                }`}>
                                  {config.type}
                                </span>
                              </td>
                              <td>{config.label}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* JSON Preview */}
            <div className="mt-8">
              <div className="card border border-light">
                <div className="card-header">
                  <h4 className="card-title">
                    <i className="fas fa-code me-2"></i>
                    Raw Configuration (JSON)
                  </h4>
                  <div className="card-toolbar">
                    <button
                      className="btn btn-sm btn-light-primary"
                      onClick={() => navigator.clipboard.writeText(JSON.stringify(mappingRules, null, 2))}
                    >
                      <i className="fas fa-copy me-1"></i>
                      Copy JSON
                    </button>
                  </div>
                </div>
                <div className="card-body">
                  <pre className="bg-light p-4 rounded" style={{ fontSize: '0.875rem', maxHeight: '300px', overflow: 'auto' }}>
                    {JSON.stringify(mappingRules, null, 2)}
                  </pre>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 text-center">
              <div className="alert alert-warning">
                <i className="fas fa-info-circle me-2"></i>
                <strong>Note:</strong> Editing mapping rules is not yet implemented.
                These rules are currently managed in the database.
              </div>
              <button className="btn btn-primary me-3" disabled>
                <i className="fas fa-edit me-2"></i>
                Edit Rules (Coming Soon)
              </button>
              <button className="btn btn-secondary" onClick={() => handlePartnerChange(selectedPartnerId)}>
                <i className="fas fa-sync me-2"></i>
                Refresh
              </button>
            </div>
          </>
        )}

        {/* No Mapping Rules */}
        {selectedPartnerId && !loading && !error && !mappingRules && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>No mapping rules found</strong> for this partner.
            Mapping rules may need to be configured in the database.
          </div>
        )}
      </div>
    </div>
  )
}

export default PartnerMappingRulesAdmin